import type { MechanicDefinition } from "./types";

export const loreDiscovery: MechanicDefinition = {
  id: "lore_discovery",
  name: "Lore Discovery",
  description: "Players uncover world lore through exploration, not exposition dumps. Director injects discovery encounters at configured density.",
  icon: "Search",
  category: "narrative",
  aiHints: {
    purpose: "Ensures lore is revealed organically through gameplay rather than exposition dumps.",
    whenToUse: "Rich lore-heavy worlds where discovery is part of the fun — mystery, fantasy, sci-fi.",
    synergies: ["quest_journal", "reactive_world", "encounter_tables"],
    conflicts: [],
    prerequisites: ["World should have lore content defined in entities and context notes"],
    tags: ["lore", "discovery", "exploration", "worldbuilding", "secrets"],
    complexity: 1,
    exampleConfig: { density: "moderate", discovery_types: ["environmental", "npc_dialogue", "items"] },
    stateVariables: [],
  },
  configSchema: [
    {
      key: "method",
      label: "Discovery Method",
      type: "select",
      options: [
        { value: "environmental", label: "Environmental (show, don't tell)" },
        { value: "collectible", label: "Collectible (find books, notes, artifacts)" },
        { value: "conversational", label: "Conversational (learn from NPCs)" },
        { value: "mixed", label: "Mixed (all of the above)" },
      ],
      default: "mixed",
    },
    {
      key: "density",
      label: "Lore Density",
      type: "select",
      options: [
        { value: "sparse", label: "Sparse (mystery is key)" },
        { value: "moderate", label: "Moderate (steady drip)" },
        { value: "rich", label: "Rich (deeply detailed world)" },
      ],
      default: "moderate",
    },
    {
      key: "gameplay_feedback",
      label: "Template Feedback",
      type: "select",
      description: "Whether gameplay lore discoveries can flow back to the base world template",
      options: [
        { value: "off", label: "Off (isolated)", description: "Gameplay lore stays in the adventure instance" },
        { value: "suggest", label: "Suggest (review queue)", description: "Notable lore creates suggestions for the world owner to review" },
        { value: "auto", label: "Auto (direct)", description: "Automatically creates entities in the base world from gameplay lore" },
      ],
      default: "off",
    },
  ],
  systemPromptSection: (config) => {
    const method = config.method || "mixed";
    const density = config.density || "moderate";
    return `## LORE DISCOVERY (Soft Mechanic — Director + Narrator)
- Method: ${method}
- Density: ${density}
- NEVER dump lore in exposition blocks. All world-building must be discovered organically.
- ${method === "environmental" ? "Embed lore in the environment: architecture, ruins, murals, nature patterns." : method === "collectible" ? "Scatter discoverable lore items: journals, inscriptions, ancient texts." : method === "conversational" ? "NPCs are the primary lore source. Each knows fragments. Some are unreliable." : "Use all channels: environment, collectibles, conversations, and gameplay mechanics to reveal lore."}
- Lore density: ${density === "sparse" ? "Leave most things unexplained. Mystery and ambiguity are features." : density === "rich" ? "The world has deep, consistent history. Reward curious players with layers of detail." : "Reveal lore at a steady pace. Balance mystery with answers."}
- Reward curiosity. Players who investigate should learn more than those who rush.

### NARRATIVE ACCESS TO KNOWLEDGE
- Lore is NOT freely available. Players must narratively access knowledge sources.
- If world context specifies WHERE knowledge is found, enforce those channels (library, sage, oracle, archive).
- If no world context, invent fitting knowledge sources: ancient libraries, scholar NPCs, temple archives, bardic traditions.
- When a player asks "what do I know about X," consider whether their character would plausibly know it.
- Unknown lore should prompt the AI to suggest where to look.
- Major lore should require effort to uncover — not handed out freely.

### WIKI & FANDOM RESEARCH PRIORITY
- For existing IP worlds, ALWAYS use search_lore_online to verify and enrich lore with canonical sources.
- Wikipedia and Fandom wikis are the PRIMARY reference — free, fast, canonical.
- Before narrating about established characters, factions, or concepts, call search_lore_online first.
- Provide the franchise parameter when known to search dedicated Fandom wikis first.
- Use wiki facts to ground narration in authentic, canonical details.

### MANDATORY CODEX RECORDING
- **EVERY TIME** the player encounters something new — an NPC, a location, an item, a faction, a piece of lore — you MUST call record_lore_discovery immediately.
- Use the correct category to classify the discovery:
  • NPC/Character met → category: "Character"
  • Location visited/learned about → category: "Geography"  
  • Faction/Organization encountered → category: "Faction"
  • Item/Artifact discovered → category: "Item"
  • Quest/Goal learned → category: "Quest"
  • Historical/Cultural/Magical knowledge → category: "History", "Magic", "Culture", etc.
- This includes: named NPCs, shops, taverns, cities, artifacts, organizations, legends, myths, scientific knowledge, and ANY other discoverable world element.
- The Codex is the player's living knowledge base. If they learned about it, it MUST be in the Codex.
- Do NOT skip this step. The player relies on the Codex to track everything they've discovered.`;
  },
  widgets: [
    {
      id: "lore_codex",
      type: "sidebar_tab",
      label: "Codex",
      icon: "BookOpen",
      component: "LoreCodexPanel",
      dataBindings: {
        discovered_lore: "variables.discovered_lore",
      },
      visibility: "when_data",
      priority: 45,
    },
  ],
  tools: [
    {
      type: "function",
      function: {
        name: "record_lore_discovery",
        description: "Record a lore discovery the player has made. This adds it to their persistent Codex for future reference. Call this EVERY TIME lore is revealed.",
        parameters: {
          type: "object",
          properties: {
            id: { type: "string", description: "Unique ID for this lore entry (use snake_case topic key)" },
            title: { type: "string", description: "Title of the lore fragment" },
            content: { type: "string", description: "The lore content the player discovered" },
            source: { type: "string", description: "How it was discovered: environmental, collectible, conversational" },
            category: { type: "string", description: "Category grouping (e.g., 'History', 'Geography', 'Magic')" },
            location: { type: "string", description: "Where it was discovered" },
          },
          required: ["id", "title", "content", "source"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "promote_lore_to_world",
        description: "Suggest a significant lore discovery for inclusion in the base world template. Only use for major, canon-worthy discoveries.",
        parameters: {
          type: "object",
          properties: {
            lore_id: { type: "string", description: "The lore entry ID to promote" },
            suggested_entity_type: { type: "string", description: "What kind of entity this lore could become (npc, location, item, faction, concept, event)" },
          },
          required: ["lore_id"],
        },
      },
    },
  ],
  initializeState: () => ({
    discovered_lore: [],
  }),
};
